import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { useNavigate } from 'react-router-dom';

interface GameLevel {
  id: string;
  name: string;
  icon: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  color: string;
  completed: boolean;
  stars: number;
}

interface SortingItem {
  id: number;
  value: number;
  color: string;
  isComparing?: boolean;
  isSorted?: boolean;
}

interface MazeCell {
  row: number;
  col: number;
  isWall: boolean;
  isStart: boolean;
  isEnd: boolean;
  isPath: boolean;
  isVisited: boolean;
}

interface Pattern {
  sequence: number[];
  answer: number;
}

interface Disk {
  id: number;
  size: number;
  color: string;
}

type Tower = Disk[];

export default function AlgorithmGames() {
  const navigate = useNavigate();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [sortingArray, setSortingArray] = useState<SortingItem[]>([]);
  const [isSorting, setIsSorting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [score, setScore] = useState(0);

  // 迷宫游戏状态
  const [maze, setMaze] = useState<MazeCell[][]>([]);
  const [robotPos, setRobotPos] = useState({ row: 1, col: 1 });
  const [mazeCompleted, setMazeCompleted] = useState(false);

  // 猜数字游戏状态
  const [targetNumber, setTargetNumber] = useState(0);
  const [guessInput, setGuessInput] = useState('');
  const [guessHistory, setGuessHistory] = useState<{ guess: number; hint: string }[]>([]);
  const [guessCount, setGuessCount] = useState(0);
  const [numberGameWon, setNumberGameWon] = useState(false);

  // 找规律游戏状态
  const [currentPattern, setCurrentPattern] = useState<Pattern | null>(null);
  const [patternAnswer, setPatternAnswer] = useState('');
  const [patternScore, setPatternScore] = useState(0);
  const [patternLevel, setPatternLevel] = useState(1);

  // 汉诺塔游戏状态
  const [towers, setTowers] = useState<Tower[]>([[], [], []]);
  const [selectedTower, setSelectedTower] = useState<number | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [diskCount, setDiskCount] = useState(3);
  const [hanoiCompleted, setHanoiCompleted] = useState(false);
  const [minMoves, setMinMoves] = useState(7);

  const games: GameLevel[] = [
    {
      id: 'bubble-sort',
      name: '泡泡排序',
      icon: 'ri-bubble-chart-line',
      description: '像泡泡一样，把大的数字浮到最上面！',
      difficulty: 'easy',
      color: 'blue',
      completed: false,
      stars: 0,
    },
    {
      id: 'maze',
      name: '迷宫探险',
      icon: 'ri-route-line',
      description: '帮助小机器人找到出口！',
      difficulty: 'medium',
      color: 'green',
      completed: false,
      stars: 0,
    },
    {
      id: 'tower-hanoi',
      name: '汉诺塔',
      icon: 'ri-stack-line',
      description: '移动圆盘，完成神奇的塔！',
      difficulty: 'hard',
      color: 'purple',
      completed: false,
      stars: 0,
    },
    {
      id: 'binary-search',
      name: '猜数字',
      icon: 'ri-search-line',
      description: '用最少的次数猜中神秘数字！',
      difficulty: 'easy',
      color: 'orange',
      completed: false,
      stars: 0,
    },
    {
      id: 'pattern',
      name: '找规律',
      icon: 'ri-grid-line',
      description: '发现数字和图形的秘密规律！',
      difficulty: 'medium',
      color: 'pink',
      completed: false,
      stars: 0,
    },
    {
      id: 'coding',
      name: '编程小达人',
      icon: 'ri-code-line',
      description: '用简单的指令控制角色完成任务！',
      difficulty: 'hard',
      color: 'indigo',
      completed: false,
      stars: 0,
    },
  ];

  const colorMap: Record<string, { bg: string; text: string; gradient: string; light: string }> = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', gradient: 'from-blue-500 to-blue-600', light: 'bg-blue-50' },
    green: { bg: 'bg-green-500', text: 'text-green-600', gradient: 'from-green-500 to-green-600', light: 'bg-green-50' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-600', gradient: 'from-purple-500 to-purple-600', light: 'bg-purple-50' },
    orange: { bg: 'bg-orange-500', text: 'text-orange-600', gradient: 'from-orange-500 to-orange-600', light: 'bg-orange-50' },
    pink: { bg: 'bg-pink-500', text: 'text-pink-600', gradient: 'from-pink-500 to-pink-600', light: 'bg-pink-50' },
    indigo: { bg: 'bg-indigo-500', text: 'text-indigo-600', gradient: 'from-indigo-500 to-indigo-600', light: 'bg-indigo-50' },
  };

  // 泡泡排序相关函数
  const generateRandomArray = () => {
    const colors = ['bg-red-400', 'bg-blue-400', 'bg-green-400', 'bg-yellow-400', 'bg-purple-400', 'bg-pink-400'];
    const array: SortingItem[] = [];
    for (let i = 0; i < 8; i++) {
      array.push({
        id: i,
        value: Math.floor(Math.random() * 90) + 10,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
    setSortingArray(array);
    setCurrentStep(0);
    setTotalSteps(0);
  };

  useEffect(() => {
    if (selectedGame === 'bubble-sort') {
      generateRandomArray();
    } else if (selectedGame === 'maze') {
      generateMaze();
    } else if (selectedGame === 'binary-search') {
      startNumberGame();
    } else if (selectedGame === 'pattern') {
      generatePattern();
    } else if (selectedGame === 'tower-hanoi') {
      initHanoi(diskCount);
    }
  }, [selectedGame]);

  const bubbleSort = async () => {
    setIsSorting(true);
    const arr = [...sortingArray];
    let steps = 0;

    for (let i = 0; i < arr.length - 1; i++) {
      for (let j = 0; j < arr.length - i - 1; j++) {
        arr[j].isComparing = true;
        arr[j + 1].isComparing = true;
        setSortingArray([...arr]);
        await new Promise(resolve => setTimeout(resolve, 500));

        if (arr[j].value > arr[j + 1].value) {
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          steps++;
          setCurrentStep(steps);
        }

        arr[j].isComparing = false;
        arr[j + 1].isComparing = false;
        setSortingArray([...arr]);
      }
      arr[arr.length - i - 1].isSorted = true;
    }
    arr[0].isSorted = true;
    setSortingArray([...arr]);
    setTotalSteps(steps);
    setScore(score + 100);
    setIsSorting(false);
  };

  // 迷宫游戏相关函数
  const generateMaze = () => {
    const rows = 10;
    const cols = 10;
    const newMaze: MazeCell[][] = [];

    for (let i = 0; i < rows; i++) {
      const row: MazeCell[] = [];
      for (let j = 0; j < cols; j++) {
        row.push({
          row: i,
          col: j,
          isWall: Math.random() < 0.25,
          isStart: i === 1 && j === 1,
          isEnd: i === rows - 2 && j === cols - 2,
          isPath: false,
          isVisited: false,
        });
      }
      newMaze.push(row);
    }

    // 确保起点和终点不是墙
    newMaze[1][1].isWall = false;
    newMaze[rows - 2][cols - 2].isWall = false;

    // 确保有路径
    for (let i = 1; i < rows - 1; i++) {
      if (Math.random() < 0.3) {
        newMaze[i][Math.floor(cols / 2)].isWall = false;
      }
    }

    setMaze(newMaze);
    setRobotPos({ row: 1, col: 1 });
    setMazeCompleted(false);
  };

  const moveRobot = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (mazeCompleted) return;

    let newRow = robotPos.row;
    let newCol = robotPos.col;

    switch (direction) {
      case 'up':
        newRow--;
        break;
      case 'down':
        newRow++;
        break;
      case 'left':
        newCol--;
        break;
      case 'right':
        newCol++;
        break;
    }

    if (
      newRow >= 0 &&
      newRow < maze.length &&
      newCol >= 0 &&
      newCol < maze[0].length &&
      !maze[newRow][newCol].isWall
    ) {
      setRobotPos({ row: newRow, col: newCol });

      const newMaze = maze.map(row => row.map(cell => ({ ...cell })));
      newMaze[newRow][newCol].isVisited = true;
      setMaze(newMaze);

      if (maze[newRow][newCol].isEnd) {
        setMazeCompleted(true);
        setScore(score + 200);
      }
    }
  };

  // 猜数字游戏相关函数
  const startNumberGame = () => {
    const target = Math.floor(Math.random() * 100) + 1;
    setTargetNumber(target);
    setGuessHistory([]);
    setGuessCount(0);
    setNumberGameWon(false);
    setGuessInput('');
  };

  const makeGuess = () => {
    const guess = parseInt(guessInput);
    if (isNaN(guess) || guess < 1 || guess > 100) return;

    const newCount = guessCount + 1;
    setGuessCount(newCount);

    let hint = '';
    if (guess === targetNumber) {
      hint = '🎉 恭喜你猜对了！';
      setNumberGameWon(true);
      const points = Math.max(300 - newCount * 20, 100);
      setScore(score + points);
    } else if (guess < targetNumber) {
      hint = '📈 太小了，再大一点！';
    } else {
      hint = '📉 太大了，再小一点！';
    }

    setGuessHistory([...guessHistory, { guess, hint }]);
    setGuessInput('');
  };

  // 找规律游戏相关函数
  const generatePattern = () => {
    const patterns = [
      // 等差数列
      { sequence: [2, 4, 6, 8], answer: 10 },
      { sequence: [5, 10, 15, 20], answer: 25 },
      { sequence: [1, 3, 5, 7], answer: 9 },
      // 等比数列
      { sequence: [2, 4, 8, 16], answer: 32 },
      { sequence: [3, 9, 27, 81], answer: 243 },
      // 斐波那契
      { sequence: [1, 1, 2, 3, 5], answer: 8 },
      { sequence: [0, 1, 1, 2, 3, 5], answer: 8 },
      // 平方数
      { sequence: [1, 4, 9, 16], answer: 25 },
      { sequence: [1, 4, 9, 16, 25], answer: 36 },
    ];

    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    setCurrentPattern(pattern);
    setPatternAnswer('');
  };

  const checkPattern = () => {
    if (!currentPattern) return;

    const answer = parseInt(patternAnswer);
    if (answer === currentPattern.answer) {
      setPatternScore(patternScore + 1);
      setPatternLevel(patternLevel + 1);
      setScore(score + 150);
      setTimeout(() => {
        generatePattern();
      }, 1500);
    }
  };

  // 汉诺塔游戏相关函数
  const initHanoi = (numDisks: number) => {
    const diskColors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400', 'bg-blue-400', 'bg-purple-400', 'bg-pink-400'];
    const initialTower: Tower = [];
    
    for (let i = numDisks; i >= 1; i--) {
      initialTower.push({
        id: i,
        size: i,
        color: diskColors[(i - 1) % diskColors.length],
      });
    }
    
    setTowers([initialTower, [], []]);
    setSelectedTower(null);
    setMoveCount(0);
    setHanoiCompleted(false);
    setMinMoves(Math.pow(2, numDisks) - 1);
  };

  const handleTowerClick = (towerIndex: number) => {
    if (hanoiCompleted) return;

    if (selectedTower === null) {
      // 选择源塔
      if (towers[towerIndex].length > 0) {
        setSelectedTower(towerIndex);
      }
    } else {
      // 移动圆盘
      if (selectedTower === towerIndex) {
        // 取消选择
        setSelectedTower(null);
      } else {
        const sourceTower = [...towers[selectedTower]];
        const targetTower = [...towers[towerIndex]];
        
        if (sourceTower.length === 0) {
          setSelectedTower(null);
          return;
        }

        const disk = sourceTower[sourceTower.length - 1];
        
        // 检查是否可以移动
        if (targetTower.length === 0 || disk.size < targetTower[targetTower.length - 1].size) {
          sourceTower.pop();
          targetTower.push(disk);
          
          const newTowers = [...towers];
          newTowers[selectedTower] = sourceTower;
          newTowers[towerIndex] = targetTower;
          
          setTowers(newTowers);
          setMoveCount(moveCount + 1);
          setSelectedTower(null);
          
          // 检查是否完成
          if (newTowers[2].length === diskCount) {
            setHanoiCompleted(true);
            const bonus = moveCount === minMoves - 1 ? 500 : 300;
            setScore(score + bonus);
          }
        } else {
          setSelectedTower(null);
        }
      }
    }
  };

  const changeDiskCount = (count: number) => {
    setDiskCount(count);
    initHanoi(count);
  };

  const handleGameClick = (gameId: string) => {
    if (gameId === 'coding') {
      // 跳转到编程小达人独立页面
      navigate('/primary/coding-game');
    } else {
      setSelectedGame(gameId);
    }
  };

  const renderGameContent = () => {
    const game = games.find(g => g.id === selectedGame);
    if (!game) return null;

    const colors = colorMap[game.color];

    // 泡泡排序
    if (selectedGame === 'bubble-sort') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">泡泡排序游戏</h3>
                <p className="text-gray-600">观察数字如何从小到大排列！</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{score}</div>
                <div className="text-sm text-gray-500">得分</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 mb-6">
              <div className="flex items-end justify-center space-x-3 h-64">
                {sortingArray.map((item) => (
                  <div key={item.id} className="flex flex-col items-center space-y-2">
                    <div className="text-sm font-bold text-gray-700">{item.value}</div>
                    <div
                      className={`w-16 ${item.color} rounded-t-lg transition-all duration-300 ${
                        item.isComparing ? 'ring-4 ring-yellow-400 scale-110' : ''
                      } ${item.isSorted ? 'ring-4 ring-green-400' : ''}`}
                      style={{ height: `${item.value * 2}px` }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{currentStep}</div>
                <div className="text-sm text-gray-600">交换次数</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{sortingArray.length}</div>
                <div className="text-sm text-gray-600">数字个数</div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={bubbleSort}
                disabled={isSorting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSorting ? '排序中...' : '开始排序'}
              </button>
              <button
                onClick={generateRandomArray}
                disabled={isSorting}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                重新生成
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
              游戏说明
            </h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5"></i>
                <p>泡泡排序会比较相邻的两个数字，如果前面的比后面的大，就交换它们的位置</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5"></i>
                <p>黄色高亮表示正在比较的数字，绿色表示已经排好序的数字</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-blue-500 mt-0.5"></i>
                <p>观察数字是如何一步步从小到大排列的，这就是算法的魔力！</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 迷宫探险
    if (selectedGame === 'maze') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">迷宫探险</h3>
                <p className="text-gray-600">帮助小机器人找到出口！</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">{score}</div>
                <div className="text-sm text-gray-500">得分</div>
              </div>
            </div>

            {/* 迷宫 */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 mb-6 flex justify-center">
              <div className="inline-block">
                {maze.map((row, i) => (
                  <div key={i} className="flex">
                    {row.map((cell, j) => (
                      <div
                        key={`${i}-${j}`}
                        className={`w-10 h-10 border border-gray-200 flex items-center justify-center text-xl transition-all ${
                          cell.isWall
                            ? 'bg-gray-800'
                            : cell.isStart
                            ? 'bg-green-400'
                            : cell.isEnd
                            ? 'bg-red-400'
                            : cell.isVisited
                            ? 'bg-blue-100'
                            : 'bg-white'
                        }`}
                      >
                        {robotPos.row === i && robotPos.col === j && (
                          <i className="ri-robot-line text-blue-600"></i>
                        )}
                        {cell.isStart && robotPos.row !== i && <i className="ri-flag-line text-white"></i>}
                        {cell.isEnd && <i className="ri-trophy-line text-white"></i>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* 控制按钮 */}
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                <button
                  onClick={() => moveRobot('up')}
                  disabled={mazeCompleted}
                  className="w-16 h-16 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-arrow-up-line text-2xl"></i>
                </button>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => moveRobot('left')}
                  disabled={mazeCompleted}
                  className="w-16 h-16 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-arrow-left-line text-2xl"></i>
                </button>
                <button
                  onClick={() => moveRobot('down')}
                  disabled={mazeCompleted}
                  className="w-16 h-16 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-arrow-down-line text-2xl"></i>
                </button>
                <button
                  onClick={() => moveRobot('right')}
                  disabled={mazeCompleted}
                  className="w-16 h-16 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-arrow-right-line text-2xl"></i>
                </button>
              </div>
            </div>

            {mazeCompleted && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-6">
                <p className="text-green-800 font-bold">🎉 恭喜你！成功找到出口了！</p>
              </div>
            )}

            <button
              onClick={generateMaze}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
            >
              生成新迷宫
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
              游戏说明
            </h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                <p>绿色方块是起点，红色方块是终点</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                <p>黑色方块是墙壁，不能通过</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                <p>使用方向键控制机器人移动，找到最短路径！</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 汉诺塔游戏
    if (selectedGame === 'tower-hanoi') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">汉诺塔游戏</h3>
                <p className="text-gray-600">将所有圆盘移动到最右边的柱子上！</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-purple-600">{score}</div>
                <div className="text-sm text-gray-500">得分</div>
              </div>
            </div>

            {/* 难度选择 */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">选择难度：</label>
              <div className="flex gap-3">
                {[3, 4, 5, 6].map((num) => (
                  <button
                    key={num}
                    onClick={() => changeDiskCount(num)}
                    disabled={hanoiCompleted && diskCount === num}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      diskCount === num
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {num} 个圆盘
                  </button>
                ))}
              </div>
            </div>

            {/* 汉诺塔可视化 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 mb-6">
              <div className="flex justify-around items-end h-80">
                {towers.map((tower, towerIndex) => (
                  <div
                    key={towerIndex}
                    onClick={() => handleTowerClick(towerIndex)}
                    className={`relative flex flex-col-reverse items-center cursor-pointer transition-all ${
                      selectedTower === towerIndex ? 'scale-105' : ''
                    }`}
                  >
                    {/* 柱子 */}
                    <div className="w-4 h-64 bg-gray-700 rounded-t-lg"></div>
                    <div className="w-48 h-4 bg-gray-800 rounded-lg"></div>
                    
                    {/* 圆盘 */}
                    <div className="absolute bottom-4 flex flex-col-reverse items-center space-y-reverse space-y-1">
                      {tower.map((disk, diskIndex) => (
                        <div
                          key={disk.id}
                          className={`${disk.color} rounded-lg h-8 transition-all duration-300 ${
                            selectedTower === towerIndex && diskIndex === tower.length - 1
                              ? 'ring-4 ring-yellow-400 shadow-lg'
                              : ''
                          }`}
                          style={{ 
                            width: `${disk.size * 30 + 40}px`,
                            marginBottom: diskIndex > 0 ? '4px' : '0'
                          }}
                        >
                          <div className="flex items-center justify-center h-full">
                            <span className="text-white font-bold text-sm">{disk.size}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 塔标签 */}
                    <div className="absolute -bottom-8 text-center">
                      <div className={`text-sm font-semibold ${
                        selectedTower === towerIndex ? 'text-purple-600' : 'text-gray-600'
                      }`}>
                        {towerIndex === 0 ? '起始塔' : towerIndex === 1 ? '辅助塔' : '目标塔'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 游戏统计 */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{moveCount}</div>
                <div className="text-sm text-gray-600">移动次数</div>
              </div>
              <div className="bg-pink-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-pink-600">{minMoves}</div>
                <div className="text-sm text-gray-600">最少步数</div>
              </div>
              <div className="bg-indigo-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-indigo-600">{diskCount}</div>
                <div className="text-sm text-gray-600">圆盘数量</div>
              </div>
            </div>

            {/* 完成提示 */}
            {hanoiCompleted && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-6">
                <p className="text-green-800 font-bold">
                  🎉 恭喜完成！你用了 {moveCount} 步
                  {moveCount === minMoves ? '，这是最优解！' : `，最优解是 ${minMoves} 步`}
                </p>
              </div>
            )}

            {/* 操作提示 */}
            {selectedTower !== null && !hanoiCompleted && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mb-6">
                <p className="text-yellow-800 font-semibold">
                  <i className="ri-information-line mr-2"></i>
                  已选择{selectedTower === 0 ? '起始塔' : selectedTower === 1 ? '辅助塔' : '目标塔'}，点击其他塔移动圆盘
                </p>
              </div>
            )}

            <button
              onClick={() => initHanoi(diskCount)}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
            >
              重新开始
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
              游戏规则
            </h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-purple-500 mt-0.5"></i>
                <p>目标：将所有圆盘从起始塔移动到目标塔</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-purple-500 mt-0.5"></i>
                <p>规则1：每次只能移动一个圆盘</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-purple-500 mt-0.5"></i>
                <p>规则2：大圆盘不能放在小圆盘上面</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-purple-500 mt-0.5"></i>
                <p>提示：可以使用辅助塔作为中转站</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-purple-500 mt-0.5"></i>
                <p>挑战：尝试用最少的步数完成！（n个圆盘的最优解是2ⁿ-1步）</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 猜数字游戏
    if (selectedGame === 'binary-search') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">猜数字游戏</h3>
                <p className="text-gray-600">我想了一个1-100之间的数字，你能猜中吗？</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-orange-600">{score}</div>
                <div className="text-sm text-gray-500">得分</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-8 mb-6">
              <div className="text-center mb-6">
                <div className="text-6xl font-bold text-orange-600 mb-4">?</div>
                <p className="text-lg text-gray-700">猜猜我想的是哪个数字</p>
                <p className="text-sm text-gray-500 mt-2">范围：1 - 100</p>
              </div>

              {!numberGameWon && (
                <div className="flex gap-3 max-w-md mx-auto">
                  <input
                    type="number"
                    value={guessInput}
                    onChange={(e) => setGuessInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && makeGuess()}
                    placeholder="输入你的猜测"
                    min="1"
                    max="100"
                    className="flex-1 px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-lg text-center"
                  />
                  <button
                    onClick={makeGuess}
                    className="px-8 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    猜！
                  </button>
                </div>
              )}

              {numberGameWon && (
                <div className="text-center">
                  <div className="text-4xl mb-4">🎉</div>
                  <p className="text-2xl font-bold text-green-600 mb-2">恭喜你猜对了！</p>
                  <p className="text-gray-600">答案是：{targetNumber}</p>
                  <p className="text-gray-600">你用了 {guessCount} 次</p>
                  <button
                    onClick={startNumberGame}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    再玩一次
                  </button>
                </div>
              )}
            </div>

            {/* 猜测历史 */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <h4 className="font-bold text-gray-900 mb-3">猜测记录：</h4>
              {guessHistory.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                >
                  <span className="font-semibold text-gray-700">第 {index + 1} 次：{item.guess}</span>
                  <span className="text-sm">{item.hint}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
              游戏说明
            </h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-orange-500 mt-0.5"></i>
                <p>电脑会随机想一个1-100之间的数字</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-orange-500 mt-0.5"></i>
                <p>根据提示调整你的猜测，用最少的次数猜中</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-orange-500 mt-0.5"></i>
                <p>提示：可以使用二分查找策略，每次猜中间值！</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 找规律游戏
    if (selectedGame === 'pattern') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">找规律游戏</h3>
                <p className="text-gray-600">发现数字序列的秘密规律！</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-pink-600">{score}</div>
                <div className="text-sm text-gray-500">得分</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-8 mb-6">
              <div className="text-center mb-6">
                <div className="inline-block bg-white rounded-lg px-4 py-2 mb-4">
                  <span className="text-sm text-gray-600">第 {patternLevel} 关</span>
                </div>
                <p className="text-lg text-gray-700 mb-6">找出下一个数字是什么？</p>

                {currentPattern && (
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    {currentPattern.sequence.map((num, index) => (
                      <div key={index} className="w-16 h-16 bg-white rounded-lg shadow-lg flex items-center justify-center">
                        <span className="text-2xl font-bold text-pink-600">{num}</span>
                      </div>
                    ))}
                    <div className="w-16 h-16 bg-pink-500 rounded-lg shadow-lg flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">?</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 max-w-md mx-auto">
                  <input
                    type="number"
                    value={patternAnswer}
                    onChange={(e) => setPatternAnswer(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && checkPattern()}
                    placeholder="输入答案"
                    className="flex-1 px-4 py-3 border-2 border-pink-200 rounded-lg focus:outline-none focus:border-pink-500 text-lg text-center"
                  />
                  <button
                    onClick={checkPattern}
                    className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    提交
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-pink-600">{patternScore}</div>
                  <div className="text-sm text-gray-600">答对题数</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{patternLevel}</div>
                  <div className="text-sm text-gray-600">当前关卡</div>
                </div>
              </div>
            </div>

            <button
              onClick={generatePattern}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
            >
              跳过这题
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
              游戏说明
            </h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5"></i>
                <p>观察数字序列，找出它们之间的规律</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5"></i>
                <p>可能是等差数列、等比数列、斐波那契数列等</p>
              </div>
              <div className="flex items-start space-x-2">
                <i className="ri-checkbox-circle-fill text-pink-500 mt-0.5"></i>
                <p>答对越多，关卡越高，难度也会增加哦！</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 其他游戏显示开发中提示
    return (
      <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
        <div className={`w-24 h-24 ${colors.light} rounded-full flex items-center justify-center mx-auto mb-6`}>
          <i className={`${game.icon} text-6xl ${colors.text}`}></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">{game.name}</h3>
        <p className="text-gray-600 mb-8">{game.description}</p>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded inline-block">
          <p className="text-sm text-gray-700">这个游戏正在开发中，敬请期待！</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navigation />

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {!selectedGame ? (
            <>
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 p-4 rounded-3xl mb-4">
                  <i className="ri-gamepad-line text-5xl text-white"></i>
                </div>
                <h1 className="text-4xl font-bold text-gray-900 mb-3">算法小游戏</h1>
                <p className="text-lg text-gray-600">在游戏中学习编程思维，培养逻辑能力！</p>
              </div>

              {/* Games Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {games.map((game) => {
                  const colors = colorMap[game.color];
                  return (
                    <div
                      key={game.id}
                      onClick={() => handleGameClick(game.id)}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer group overflow-hidden"
                    >
                      <div className={`${colors.light} p-8`}>
                        <div className={`w-16 h-16 bg-gradient-to-r ${colors.gradient} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <i className={`${game.icon} text-3xl text-white`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{game.name}</h3>
                        <p className="text-sm text-gray-600">{game.description}</p>
                      </div>

                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <span className={`px-3 py-1 ${colors.light} ${colors.text} rounded-full text-xs font-semibold`}>
                            {game.difficulty === 'easy' ? '简单' : game.difficulty === 'medium' ? '中等' : '困难'}
                          </span>
                          <div className="flex space-x-1">
                            {[1, 2, 3].map((star) => (
                              <i
                                key={star}
                                className={`ri-star-${star <= game.stars ? 'fill' : 'line'} text-yellow-400`}
                              ></i>
                            ))}
                          </div>
                        </div>

                        <button className={`w-full px-6 py-3 bg-gradient-to-r ${colors.gradient} text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}>
                          开始游戏
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Info Section */}
              <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">为什么要学习算法？</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-brain-line text-3xl text-blue-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">培养逻辑思维</h4>
                    <p className="text-sm text-gray-600">学会分析问题，找到解决方案</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-lightbulb-line text-3xl text-purple-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">提升创造力</h4>
                    <p className="text-sm text-gray-600">用不同的方法解决同一个问题</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-rocket-line text-3xl text-pink-600"></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">为未来做准备</h4>
                    <p className="text-sm text-gray-600">编程是未来世界的重要技能</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Back Button */}
              <button
                onClick={() => setSelectedGame(null)}
                className="mb-6 px-6 py-3 bg-white text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center space-x-2 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                <span>返回游戏列表</span>
              </button>

              {renderGameContent()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
